/**
 * Copies a rendered Ask Addy message to the clipboard as clean, rich content.
 *
 * The raw message is Markdown where each document reference looks like
 * `[2024 W2.pdf](<https://...very-long-signed-url...>)`. Copying that raw
 * Markdown leaks the entire signed URL inline after every document name, which
 * reads as noisy and sloppy when pasted into an email.
 *
 * Instead we copy the *rendered* DOM node, mirroring a manual cursor
 * copy/paste: `text/html` keeps document names as clean clickable hyperlinks,
 * and `text/plain` carries only the visible text (no raw URLs). Falls back to
 * plain text when the rich clipboard API is unavailable.
 */

/**
 * Extracts the visible text of a rendered node (no href URLs).
 *
 * @param {HTMLElement|null} element
 * @returns {string}
 */
// Rendered controls that are not part of the answer, such as the
// "Show me on screen" buttons, carry this attribute and are never copied.
const COPY_SKIP_SELECTOR = "[data-copy-skip]";

export const getRenderedPlainText = (element) => {
  if (!element) {
    return "";
  }

  // innerText follows the rendered layout (line breaks) and leaves out hidden
  // nodes, so skipped controls are hidden only for the length of the read.
  const skipped = Array.from(element.querySelectorAll(COPY_SKIP_SELECTOR));
  const previousDisplay = skipped.map((node) => node.style.display);
  skipped.forEach((node) => {
    node.style.display = "none";
  });
  try {
    const renderedText = typeof element.innerText === "string" ? element.innerText : "";
    // Without a layout engine (tests) there is no innerText: drop the skipped
    // nodes from a copy instead.
    return (renderedText || getCopyableClone(element).textContent || "").trim();
  } finally {
    skipped.forEach((node, index) => {
      node.style.display = previousDisplay[index];
    });
  }
};

const getCopyableClone = (element) => {
  const clone = element.cloneNode(true);
  clone.querySelectorAll(COPY_SKIP_SELECTOR).forEach((node) => node.remove());
  return clone;
};

/**
 * Writes the rendered content of an element to the clipboard as rich HTML plus
 * clean plain text. Falls back to plain text, then reports success.
 *
 * @param {HTMLElement|null} element
 * @returns {Promise<boolean>} whether anything was copied
 */
export const copyRenderedMessage = async (element) => {
  if (!element) {
    return false;
  }

  const html = getCopyableClone(element).innerHTML;
  const text = getRenderedPlainText(element);

  const canUseRichClipboard =
    typeof ClipboardItem !== "undefined" &&
    typeof navigator !== "undefined" &&
    !!navigator.clipboard &&
    typeof navigator.clipboard.write === "function";

  if (canUseRichClipboard) {
    try {
      const clipboardItem = new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([text], { type: "text/plain" }),
      });
      await navigator.clipboard.write([clipboardItem]);

      return true;
    } catch (err) {
      // Rich clipboard failed (permissions, unsupported types); fall back below.
    }
  }

  if (
    typeof navigator === "undefined" ||
    !navigator.clipboard ||
    typeof navigator.clipboard.writeText !== "function"
  ) {
    return false;
  }

  try {
    await navigator.clipboard.writeText(text);

    return true;
  } catch (err) {
    return false;
  }
};
